import {
  getAllCityInfo,
  getMultiCityInfos,
  getAllAvailableQuarters,
  toggleMultiCityView,
} from '../../actions/PageActions'
import { userInit, logoutUser } from '../../actions/UserActions'
import {
  getFilteredCities,
  updateCurrentCityId,
  updateSelectedQuarter,
} from '../../actions/UserActions'
import { getPageContext } from '../../ai/utils/getPageContext'
import {
  LoadingScreen,
  PageWrapper,
  NewFeatureSpotlight,
} from '../../components'
import { ErrorBoundary } from '../../components/ErrorBoundary'
import {
  getSelectedQuarterString,
  parseSelectedQuarterValue,
} from '../../constants/Constants'
import { gAnalytics } from '../../helpers/analytics'
import {
  AdminView,
  AgentAdmin,
  Cities,
  CitiesAdmin,
  CityProfile,
  CityScores,
  ConsiderationComparison,
  Dashboard,
  DimensionComparison,
  IndicatorComparison,
  ForgotPassword,
  Login,
  Methodology,
  SustainabilityDevelopmentGoals,
  Profile,
  Register,
  ResetPassword,
  UrbanMonitoringFramework,
  Users,
  OrganizationsList,
  OrganizationCreate,
  OrganizationEdit,
} from '../../pages'
import LanguageProvider from '../../state/contexts/LanguageProvider'
import useLanguage from '../../state/hooks/useLanguage'
import { ChatApp } from '@innovasium-2014/chat-widget'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
  Redirect,
  BrowserRouter as Router,
  Route,
  Switch,
} from 'react-router-dom'
import { bindActionCreators } from 'redux'

const App = ({
  actions,
  pageStore,
  userStore,
  modalStore,
  cityStore,
  milestoneStore,
}) => {
  const pagePath = window.location.pathname.split('/')[1]
  const userHasSession = userStore.get('hasSession')
  const loginUserExists = userStore.get('loginUserExists')
  const selectedCityId = userStore.getIn(['selectedCityId'])
  const selectedQuarter = userStore.getIn(['selectedQuarter'])
  const allQuarters = pageStore.getIn(['allQuarters']).toJS()
  const userId = userStore.getIn(['userInfo', 'id'])
  const subscriptedCityIds = useMemo(
    () =>
      (userStore.get('subscriptions') || []).map((s) => s.get('city_id')).toJS(),
    [userStore.get('subscriptions')]
  )
  const cities = pageStore.get('multiCityInfos')
  const cityCountryMap = pageStore.get('cityCountryMap')
  const isMultiCity = pageStore.get('isMultiCity')
  const userEmail = userStore.getIn(['userInfo', 'email']) || ''
  const isAllowedDomain =
    /@(innovasium\.com|citiiq\.com|blythgroup\.ca)$/i.test(userEmail)
  const isAdminUser = userStore.getIn(['role', 'id']) === 2
  const isAdminPage = [
    'agent-admin',
    'organizations',
    'users',
    'cities',
    'cities-admin',
  ].includes(pagePath)

  const allowedCities = useMemo(
    () =>
      subscriptedCityIds.map((id) => {
        const city = cityCountryMap && cityCountryMap.getIn(['obj', String(id)])
        if (!city) return { id, name: `City ${id}` }
        return {
          id,
          name: city.get('name'),
          province: city.get('province') || null,
          country: city.get('country') || null,
        }
      }),
    [subscriptedCityIds, cityCountryMap]
  )

  const selectedCity = isMultiCity
    ? { all: true, cities: allowedCities }
    : selectedCityId
    ? allowedCities.find((c) => c.id === selectedCityId) || {
        id: selectedCityId,
        name: `City ${selectedCityId}`,
      }
    : null
  const [currentPage, setCurrentPage] = useState(pagePath)

  /*
   * toJS() deep-converts the whole structure and returns a new object every
   * time, so doing it inline made every consumer see changed props on every
   * render. Immutable values have stable identity, so keying the conversions
   * on them means they only rerun when the data actually changes.
   */
  const allInfo = useMemo(
    () => pageStore.get('allInfo')?.toJS(),
    [pageStore.get('allInfo')]
  )
  const considerationsComparison = useMemo(
    () => pageStore.get('considerationsComparison')?.toJS(),
    [pageStore.get('considerationsComparison')]
  )
  const multiCityInfosJS = useMemo(
    () => pageStore.get('multiCityInfos')?.toJS(),
    [pageStore.get('multiCityInfos')]
  )
  const cityCountryMapJS = useMemo(
    () => cityCountryMap?.toJS(),
    [cityCountryMap]
  )
  const sdgJS = useMemo(
    () => cityStore.get('sdg')?.toJS?.() || cityStore.get('sdg') || null,
    [cityStore.get('sdg')]
  )
  const dimensionsComparison = useMemo(
    () => pageStore.get('dimensionsComparison')?.toJS(),
    [pageStore.get('dimensionsComparison')]
  )
  const milestones = useMemo(
    () => milestoneStore.get('allMilestones')?.toJS() || [],
    [milestoneStore.get('allMilestones')]
  )
  const indicatorChartFilters = useMemo(
    () => pageStore.get('indicatorChartFilters')?.toJS(),
    [pageStore.get('indicatorChartFilters')]
  )
  const indicatorChartData = useMemo(
    () => pageStore.get('indicatorChartData')?.toJS(),
    [pageStore.get('indicatorChartData')]
  )

  const storeData = useMemo(
    () => ({
      isMultiCity,
      selectedQuarter,
      allInfo,
      considerationsComparison,
      selectedDimensionId: 1,
      multiCityInfos: multiCityInfosJS,
      cityCountryMap: cityCountryMapJS,
      filteredCities: userStore.getIn(['filteredCities']) || [],
      drawerOpen: modalStore.get('drawerOpen'),
      drawerConsideration: modalStore.get('drawerConsideration'),
      sdg: sdgJS,
      dimensionsComparison,
      milestones,
      indicatorChartFilters,
      indicatorChartData,
    }),
    [
      isMultiCity,
      selectedQuarter,
      allInfo,
      considerationsComparison,
      multiCityInfosJS,
      cityCountryMapJS,
      userStore.getIn(['filteredCities']),
      modalStore.get('drawerOpen'),
      modalStore.get('drawerConsideration'),
      sdgJS,
      dimensionsComparison,
      milestones,
      indicatorChartFilters,
      indicatorChartData,
    ]
  )
  const [loading, setLoading] = useState(true)
  const [initMultiCityInfoRequest, setInitMultiCityInfoRequest] =
    useState(false)

  function handlePrivateUser() {
    window.onbeforeunload = function (event) {
      event.preventDefault()
      actions.logoutUser()
      return true
    }
  }

  useEffect(() => {
    if (loginUserExists) {
      if (userStore.getIn(['role', 'title']) === 'private_user') {
        handlePrivateUser()
      }

      actions.getFilteredCities(userId)
      actions.getAllAvailableQuarters(subscriptedCityIds)
      // if initial login, default set selectedCityId to first item in array
      if (!selectedCityId && subscriptedCityIds.length > 0) {
        actions.updateCurrentCityId(subscriptedCityIds[0])
        if (subscriptedCityIds.length === 1) {
          actions.toggleMultiCityView(false)
        }
      }
    }
    // eslint-disable-next-line
  }, [loginUserExists, selectedQuarter])

  useEffect(() => {
    if (loginUserExists) {
      // if initial login, default set selectedQuarter to latest
      if (!selectedQuarter && allQuarters.length > 0) {
        const value = getSelectedQuarterString(allQuarters[0])
        actions.updateSelectedQuarter(value)
      }
      if (selectedQuarter && cities.size === 0 && !initMultiCityInfoRequest) {
        const { year, quarter } = parseSelectedQuarterValue(selectedQuarter)
        actions.getMultiCityInfos(subscriptedCityIds, year, quarter)
        setInitMultiCityInfoRequest(true)
      }
    } else {
      if (initMultiCityInfoRequest) {
        setInitMultiCityInfoRequest(false)
      }
    }
    // eslint-disable-next-line
  }, [loginUserExists, selectedQuarter, allQuarters, cities])

  useEffect(() => {
    if (loginUserExists && selectedCityId && selectedQuarter) {
      const { year, quarter } = parseSelectedQuarterValue(selectedQuarter)
      actions.getAllCityInfo(selectedCityId, year, quarter)
      actions.getMultiCityInfos(subscriptedCityIds, year, quarter)
    }
    // eslint-disable-next-line
  }, [selectedCityId, selectedQuarter])

  useEffect(() => {
    async function initUser() {
      setLoading(true)
      const response = await actions.userInit()
      if (response.InitUser.exists) {
        // Pre-warm the Expanse Agent Admin embed lambda for admins only, so
        // by the time they navigate to /agent-admin (often minutes later)
        // the cold-start has already happened. Gated by the same role check
        // that gates the /agent-admin route below — we don't want to ping
        // the Expanse origin from non-admin sessions that will never use it.
        const isAdmin = response.InitUser?.role?.title === 'admin'
        const embedUrl = process.env.REACT_APP_INNO_AI_EMBED_URL
        if (isAdmin && embedUrl) {
          try {
            const origin = new URL(embedUrl).origin
            if (
              !document.head.querySelector(
                `link[rel="preconnect"][href="${origin}/"]`
              )
            ) {
              const link = document.createElement('link')
              link.rel = 'preconnect'
              link.href = origin
              link.crossOrigin = 'anonymous'
              document.head.appendChild(link)
            }
            fetch(`${origin}/api/embed/warmup`, {
              method: 'GET',
              mode: 'no-cors',
              credentials: 'omit',
              cache: 'no-store',
            }).catch(() => {})
          } catch {
            /* invalid embed URL — ignore */
          }
        }
      }
      gAnalytics('pageview', [document.location.pathname])
      setLoading(false)
    }
    initUser()
    // eslint-disable-next-line
  }, [userHasSession])

  useEffect(() => {
    if (pagePath !== currentPage) {
      window.scrollTo(0, 0)
      setCurrentPage(pagePath)
      gAnalytics('pageview', [document.location.pathname])
    }
  }, [pagePath, currentPage])

  function waitToRender() {
    if (userStore.get('loadingUser')) return <LoadingScreen />
    if (!userStore.get('hasSession')) return <Login />
    return <Redirect to="/dashboard" />
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <ErrorBoundary>
      <LanguageProvider userId={userId}>
        <Router>
          <Switch>
            <Route path="/register" component={Register} />
            <Route path="/forgot-password/:token" component={ResetPassword} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <PrivateRoute path="/city-profile" component={CityProfile} />
            <PrivateRoute path="/city-scores" component={CityScores} />
            <PrivateRoute path="/dashboard" component={Dashboard} />
            <PrivateRoute
              path="/consideration-comparison"
              component={ConsiderationComparison}
            />
            <PrivateRoute
              path="/dimension-comparison"
              component={DimensionComparison}
            />
            <PrivateRoute
              path="/indicator-comparison"
              component={IndicatorComparison}
            />
            <PrivateRoute path="/profile" component={Profile} />
            <PrivateRoute path="/methodology" component={Methodology} />
            <PrivateRoute
              path="/sdg"
              component={SustainabilityDevelopmentGoals}
            />
            <PrivateRoute
              path="/urban-monitoring-framework"
              component={UrbanMonitoringFramework}
            />
            {userStore.getIn(['role', 'title']) === 'admin' && (
              <PrivateRoute path="/admin-view" component={AdminView} />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute path="/agent-admin" component={AgentAdmin} />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute
                exact
                path="/organizations"
                component={OrganizationsList}
              />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute
                path="/organizations/new"
                component={OrganizationCreate}
              />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute
                path="/organizations/:id/edit"
                component={OrganizationEdit}
              />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute path="/users" component={Users} />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute path="/cities" component={Cities} />
            )}
            {isAdminUser && isAllowedDomain && (
              <PrivateRoute path="/cities-admin" component={CitiesAdmin} />
            )}
            <Route render={waitToRender} />
          </Switch>
        </Router>
        {Boolean(userHasSession) &&
          process.env.REACT_APP_INNO_AI_API_KEY &&
          !isAdminPage && (
            <ChatAppWithLanguage
              allowedCities={allowedCities}
              selectedCity={selectedCity}
              currentPage={currentPage}
              storeData={storeData}
              userId={userId}
            />
          )}
        {Boolean(userHasSession) &&
          process.env.REACT_APP_INNO_AI_API_KEY &&
          !isAdminPage && (
            <NewFeatureSpotlight
              featureId="ai-chat-agent"
              description="Meet your new AI assistant! Click this icon anytime to ask questions about your city data, get insights, or explore trends. It's here to help you get the most out of CitiIQ."
              targetSelector="#innovasium-chat-fab"
              userId={userId}
            />
          )}
      </LanguageProvider>
    </ErrorBoundary>
  )
}

const ChatAppWithLanguage = ({
  allowedCities,
  selectedCity,
  currentPage,
  storeData,
  userId,
}) => {
  const { language } = useLanguage()
  const locale = language.locale || 'en'
  const pageContext = getPageContext(currentPage, { ...storeData, locale })

  return (
    <ChatApp
      apiKey={process.env.REACT_APP_INNO_AI_API_KEY}
      userId={userId}
      dataJson={{
        context: {
          allowedCities,
          selectedCity,
          currentPage: pageContext,
          userLanguage: locale,
        },
      }}
    />
  )
}

function mapStateToProps(state) {
  return {
    userStore: state.UserStore,
    pageStore: state.PageStore,
    modalStore: state.ModalStore,
    cityStore: state.CityStore,
    milestoneStore: state.MilestoneStore,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        userInit,
        logoutUser,
        getAllCityInfo,
        getFilteredCities,
        getMultiCityInfos,
        getAllAvailableQuarters,
        updateCurrentCityId,
        updateSelectedQuarter,
        toggleMultiCityView,
      },
      dispatch
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(App)

const PrivateRouteComponent = ({ component, path, userStore, ...props }) => {
  const Component = component
  return (
    <Route
      path={path}
      render={(props) => {
        if (userStore.get('loadingUser')) {
          return <LoadingScreen />
        }
        if (userStore.get('hasSession')) {
          return (
            <PageWrapper>
              <ErrorBoundary>
                <Component {...props} />
              </ErrorBoundary>
            </PageWrapper>
          )
        }
        return <Login />
      }}
    />
  )
}

const PrivateRoute = connect(
  mapStateToProps,
  mapDispatchToProps
)(PrivateRouteComponent)
